import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Translations from '@helpers/translations'
import { ReturnsHeaderImage } from '@components/returns/ReturnsHeaderImage'
import Notification from '@components/ui/Notification'
import ItemsSelection, { ItemSelectionOnSubmitProps } from './ItemsSelection'
import CarrierSelection from './CarrierSelection'
import ReturnsHeader from './ReturnsHeader'
import OrderForm from './OrderForm'
import styles from './styles.module.scss'
import formData from './formData'
import Footer from '@components/Footer'
import { checkStoreIs, prefixLocale } from '@helpers/env'
import { stripMtoSuffix } from '@api/returns/helpers'

type SelectedOrder = Omit<ItemSelectionOnSubmitProps, 'queryParams'>

function toQueryString(
  params: Record<string, string | number | boolean | null | undefined>
): string {
  const cleanedEntries = Object.entries(params)
    .filter(([, value]) => value != null) // drop null/undefined
    .map(([key, value]) => [key, String(value)] as [string, string])

  return new URLSearchParams(cleanedEntries).toString()
}

export const isRightStoreForOrder = (orderNumber: string) => {
  const orderLocale = orderNumber.match(/[a-z]{2}/i)?.[0]
  const locale = orderLocale?.toLowerCase()

  if (locale === 'us' && !checkStoreIs('us')) return false
  if (locale === 'eu' && !checkStoreIs('eu')) return false
  return true
}

const ReturnPageContent = () => {
  const router = useRouter()
  const {
    order_number: rawOrderNumber,
    postcode,
    successReturn,
    admin
  } = router.query as {
    order_number: string | undefined
    postcode: string | undefined
    successReturn: string | undefined
    admin: string | undefined
  }
  const orderNumber = rawOrderNumber ? stripMtoSuffix(rawOrderNumber) : undefined
  const [isClient, setIsClient] = useState(false)
  const [order, setOrder] = useState<SelectedOrder>()

  useEffect(() => {
    setIsClient(true)
  }, [])

  const onSubmit = ({
    salesOrderId,
    customerAddress,
    productsToReturn,
    carriers,
    queryParams
  }: ItemSelectionOnSubmitProps) => {
    setOrder({ productsToReturn, carriers, salesOrderId, customerAddress })
    const { query } = router

    const mergedQuery = {
      ...query,
      ...queryParams,
      pagesHandle: undefined,
      pageHandle: undefined,
      error: undefined
    }

    let href = `${prefixLocale()}/pages/returns?${toQueryString(mergedQuery)}`
    const orderNumber = queryParams.order_number

    if (orderNumber && !isRightStoreForOrder(orderNumber)) {
      href = `/pages/returns?error=true`
    }

    router.push(href, undefined, { shallow: true })
  }

  if (process.env.RETURNS_MAINTENANCE_MODE === 'true' && admin !== 'true') {
    return (
      <>
        <ReturnsHeaderImage content={formData.header} />
        <Notification
          type="warning"
          content={
            <>
              <p>
                {Translations.localise(
                  'pages.returns.returns-notifications.maintenance-mode.copy-1'
                )}
              </p>
              <p>
                {Translations.localise(
                  'pages.returns.returns-notifications.maintenance-mode.copy-2'
                )}
              </p>
            </>
          }
        />
      </>
    )
  }

  if (orderNumber && postcode && order?.carriers && order?.customerAddress) {
    return (
      <CarrierSelection
        headerImage={formData.header.image}
        orderNumber={orderNumber}
        postcode={postcode}
        carriers={order.carriers}
        productsToReturn={order.productsToReturn}
        salesOrderId={order.salesOrderId}
        customerAddress={order.customerAddress}
      />
    )
  }

  if (orderNumber && postcode) {
    return (
      <ItemsSelection
        successReturn={successReturn}
        orderNumber={orderNumber}
        postcode={postcode}
        onSubmit={onSubmit}
      />
    )
  }

  return isClient && <OrderForm formData={formData} onSubmit={onSubmit} />
}

const ReturnPage = () => {
  return (
    <div className={styles.returns__wrapper}>
      <ReturnsHeader />
      <div className={styles.returns__contentWrapper}>
        <ReturnPageContent />
      </div>
      <Footer />
    </div>
  )
}

export default ReturnPage
